import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { waLink, track } from "@/lib/site";

export function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export function SectionHead({ eyebrow, title, sub, center = false }) {
  return (
    <Reveal className={center ? "text-center" : ""}>
      <span className="eyebrow">{eyebrow}</span>
      <span className={`mt-3 block h-[2px] w-12 bg-gold ${center ? "mx-auto" : ""}`} />
      <h2 className="mt-5 font-serif text-3xl font-semibold leading-tight text-navy sm:text-4xl">{title}</h2>
      {sub && <p className={`mt-4 max-w-2xl text-[15px] leading-relaxed text-mist ${center ? "mx-auto" : ""}`}>{sub}</p>}
    </Reveal>
  );
}

export function PageHero({ eyebrow, title, sub, children }) {
  return (
    <section className="border-b border-line bg-sand">
      <div className="container-x py-14 md:py-20">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="eyebrow">{eyebrow}</span>
          <span className="mt-3 block h-[2px] w-12 bg-gold" />
          <h1 className="mt-5 max-w-3xl font-serif text-4xl font-semibold leading-tight text-navy sm:text-5xl">{title}</h1>
          {sub && <p className="mt-4 max-w-2xl text-base leading-relaxed text-mist">{sub}</p>}
          {children}
        </motion.div>
      </div>
    </section>
  );
}

export function TickList({ items, className = "" }) {
  return (
    <ul className={`space-y-3 ${className}`}>
      {items.map((t) => (
        <li key={t} className="flex items-start gap-3 text-sm leading-relaxed text-ink">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold-dark">
            <Check size={13} strokeWidth={3} />
          </span>
          {t}
        </li>
      ))}
    </ul>
  );
}

export function CTABand({
  title = "Ready to source quality food products from India?",
  sub = "Share your requirement with our export team — we will respond with specifications, packaging options and a commercial quotation.",
}) {
  return (
    <section className="section-pad bg-navy" data-testid="cta-band">
      <div className="container-x flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
        <div className="max-w-2xl">
          <h2 className="font-serif text-3xl font-semibold leading-tight text-ivory sm:text-4xl">{title}</h2>
          <p className="mt-3 text-sm leading-relaxed text-ivory/70">{sub}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/quote" data-testid="cta-band-quote-btn" className="btn-gold">
            Request a Quote <ArrowRight size={16} />
          </Link>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-band-whatsapp-btn"
            onClick={() => track("whatsapp_click")}
            className="btn-outline-light"
          >
            WhatsApp Us
          </a>
        </div>
      </div>
    </section>
  );
}
